import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, CircularProgress, Skeleton, Stack, Typography } from '@mui/material';

import { varAlpha } from 'src/theme/styles';

const DEFAULT_ROW_COUNT = 4;
const ROW_OPACITIES = [0.08, 0.06, 0.04, 0.02];

const skeletonBarSx = (width: number | string, opacity: number) => ({
  width,
  height: 12,
  borderRadius: 0.5,
  bgcolor: (theme: { vars: { palette: { grey: { '500Channel': string } } } }) =>
    varAlpha(theme.vars.palette.grey['500Channel'], opacity),
});

const skeletonPillSx = (width: number | string, opacity: number) => ({
  width,
  height: 14,
  borderRadius: 0.5,
  bgcolor: (theme: { vars: { palette: { grey: { '500Channel': string } } } }) =>
    varAlpha(theme.vars.palette.grey['500Channel'], opacity),
});

interface InspectorAvailabilityGridSkeletonLoadingRowProps {
  opacity: number;
}

export const InspectorAvailabilityGridSkeletonLoadingRow = memo(
  ({ opacity }: InspectorAvailabilityGridSkeletonLoadingRowProps) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 1,
        width: '100%',
      }}
    >
      <Skeleton
        variant="circular"
        animation="pulse"
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          bgcolor: (theme) => varAlpha(theme.vars.palette.grey['500Channel'], opacity),
        }}
      />

      <Stack spacing={1} sx={{ flexShrink: 0 }}>
        <Skeleton variant="rounded" animation="pulse" sx={skeletonBarSx(160, opacity)} />
        <Skeleton variant="rounded" animation="pulse" sx={skeletonBarSx(120, opacity)} />
      </Stack>

      <Stack spacing={0.5} sx={{ flexShrink: 0 }}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Skeleton variant="rounded" animation="pulse" sx={skeletonPillSx(120, opacity)} />
          <Skeleton variant="rounded" animation="pulse" sx={skeletonPillSx(80, opacity)} />
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Skeleton variant="rounded" animation="pulse" sx={skeletonPillSx(80, opacity)} />
          <Skeleton variant="rounded" animation="pulse" sx={skeletonPillSx(120, opacity)} />
        </Box>
      </Stack>

      <Stack spacing={1} sx={{ width: 200, flexShrink: 0 }}>
        <Skeleton variant="rounded" animation="pulse" sx={skeletonBarSx('100%', opacity)} />
        <Skeleton variant="rounded" animation="pulse" sx={skeletonBarSx(120, opacity)} />
      </Stack>

      <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="rounded" animation="pulse" sx={skeletonBarSx('100%', opacity)} />
      </Stack>

      <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="rounded" animation="pulse" sx={skeletonBarSx('100%', opacity)} />
      </Stack>

      <Stack spacing={1} sx={{ flex: 1, minWidth: 0, opacity: opacity <= 0.04 ? 0 : 1 }}>
        <Skeleton variant="rounded" animation="pulse" sx={skeletonBarSx('100%', opacity)} />
      </Stack>
    </Box>
  )
);

InspectorAvailabilityGridSkeletonLoadingRow.displayName =
  'InspectorAvailabilityGridSkeletonLoadingRow';

interface InspectorAvailabilityGridSkeletonLoadingProps {
  message?: string;
  rowCount?: number;
}

export const InspectorAvailabilityGridSkeletonLoading = memo(
  ({ message, rowCount = DEFAULT_ROW_COUNT }: InspectorAvailabilityGridSkeletonLoadingProps) => {
    const { t } = useTranslation('shuffle');

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          overflow: 'hidden',
        }}
      >
        {Array.from({ length: rowCount }, (_, index) => (
          <InspectorAvailabilityGridSkeletonLoadingRow
            key={index}
            opacity={ROW_OPACITIES[index] ?? ROW_OPACITIES[ROW_OPACITIES.length - 1]}
          />
        ))}
      </Box>
    );
  }
);

InspectorAvailabilityGridSkeletonLoading.displayName = 'InspectorAvailabilityGridSkeletonLoading';
